import React, { useEffect, useState } from 'react'
import { useAtomValue } from 'jotai'
import { SubmitHandler } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Box, Button, DataListItem, Dialog, Input } from '@node-space/ui-components'
import { isTestEnvironment, roundNumberWithCommas, stringToDecimal } from 'utils/utils'
import { MAX_GBP_PAYMENT_AMOUNT } from '../FiatSend/constants'
import {
  selectedBvnkNetworkBeneficiaryAtom,
  selectedFiatBeneficiaryAtom,
  selectedWalletAtom,
} from './atoms/atoms'
import usePaymentFeeRequest from './hooks/usePaymentFeeRequest'
import { PayActionProps } from './Pay'
import { getRecipientName } from './utils/payFormUtils'

const PayAmount = ({
  form,
  loading,
  payFees,
  feeError,
  confirmAmount,
  dismissAction,
  setStep,
  registerFormField,
}: PayActionProps) => {
  const selectedBeneficiary = useAtomValue(selectedFiatBeneficiaryAtom)
  const selectedBvnkNetworkBeneficiary = useAtomValue(selectedBvnkNetworkBeneficiaryAtom)
  const sourceWallet = useAtomValue(selectedWalletAtom)

  const { t } = useTranslation()
  const formValues = form.watch()
  const [transactionFee, setTransactionFee] = useState('')
  const [feeAmount, setFeeAmount] = useState(null)

  const { maxPayoutAmount, isFetchingMaxPayoutAmount, fetchingMaxPayoutAmountError } =
    usePaymentFeeRequest({
      paymentReference: formValues?.reference,
      selectedBeneficiary,
      beneficiaryReference: formValues?.beneficiarySelection,
    })

  useEffect(() => {
    form.setFocus('amount')
  }, [form.setFocus])

  useEffect(() => {
    if (payFees?.fee) {
      setFeeAmount(payFees?.fee)
    } else {
      setFeeAmount(0)
    }
  }, [sourceWallet, feeAmount, payFees])

  const validateAmount = (amount: number | string, onBlur = isTestEnvironment) => {
    const error = checkMaxAmount(amount)

    if (error) {
      form.setError('amount', { message: error })
    } else {
      form.clearErrors('amount')
      form.setValue('amount', stringToDecimal(amount.toString()))
    }
  }

  useEffect(() => {
    if (payFees?.fee?.value && !Number.isNaN(payFees?.fee?.value)) {
      setTransactionFee(`${roundNumberWithCommas(payFees?.fee?.value)} ${sourceWallet?.currency}`)
    } else {
      setTransactionFee(`${roundNumberWithCommas(0)} ${sourceWallet?.currency}`)
    }
  }, [payFees])

  useEffect(() => {
    if (feeError) {
      const isZeroAmountError = feeError?.data?.details?.errors?.['amount.value']

      form.setError('amount', {
        message: isZeroAmountError ? t('zeroAmountError') : feeError?.data?.message,
      })
    } else {
      form.clearErrors('amount')
    }
  }, [feeError, formValues?.amount])

  const checkMaxAmount = (amount: number | string) => {
    const amountErrorMaximumFees =
      sourceWallet.balance &&
      sourceWallet &&
      t('amountErrorMaximumPay', {
        amount: roundNumberWithCommas(maxPayoutAmount),
        currencyCode: sourceWallet?.currency,
      })

    if (Number(amount) > maxPayoutAmount) {
      return amountErrorMaximumFees
    }
  }

  const onSubmit: SubmitHandler<typeof formValues> = async () => {
    confirmAmount()
  }

  const handleAmountOnChange = (e: React.ChangeEvent<HTMLInputElement>, onBlur = false) => {
    !onBlur && form.setValue('amount', e.target.value)

    const amount = Number(e.target.value)
    const isGbpPayment = sourceWallet?.currency === 'GBP'

    if (!isNaN(amount) && isGbpPayment && amount > MAX_GBP_PAYMENT_AMOUNT) {
      form.setError('amount', { message: t('wallets.fiatSend.maximumGbpAmount') })
      return
    }

    validateAmount(stringToDecimal(e.target.value), onBlur)
  }

  return (
    <Dialog
      open
      title={`${t('pay')} ${selectedBvnkNetworkBeneficiary ? selectedBvnkNetworkBeneficiary?.details?.name : getRecipientName(selectedBeneficiary)}`}
      onClose={dismissAction}
      type="utility"
      ActionButtons={[
        <Button
          key="continue"
          testId={t('continue')}
          onClick={form.handleSubmit(onSubmit)}
          loading={loading || isFetchingMaxPayoutAmount}
          disabled={
            !formValues.amount ||
            !!form.formState.errors.amount ||
            !!feeError ||
            !!fetchingMaxPayoutAmountError
          }
        >
          {t('continue')}
        </Button>,
        <Button key="back" variant="secondary" onClick={() => setStep('form')}>
          {t('back')}
        </Button>,
      ]}
    >
      <form>
        <Box className="space-y-6">
          <Input
            {...registerFormField('amount')}
            label={t('amount')}
            testId="amount"
            rightSuffixText={sourceWallet?.currency}
            type="number"
            required
            helperText={
              !!maxPayoutAmount &&
              !feeError &&
              t('amountMaximumPay', {
                amount: roundNumberWithCommas(maxPayoutAmount),
                currencyCode: sourceWallet.currency,
              })
            }
            onBlur={e => handleAmountOnChange(e, true)}
            onChange={e => handleAmountOnChange(e)}
            error={!!form.formState?.errors?.amount?.message}
            errorText={form.formState?.errors?.amount?.message}
            value={formValues?.amount}
          />
        </Box>
        <DataListItem
          id="transaction-fee"
          testId="transaction-fee"
          title={t('transactionFee')}
          body={transactionFee}
        />
      </form>
    </Dialog>
  )
}

export default PayAmount
